import * as THREE from "three";
import type { FireResult, Weapon, WeaponHudState } from "./weapon";
import type { ProjectileManager } from "./projectile";
import { bus } from "../core/bus";
import { Audio } from "../sfx/audio";
import type { WeaponMods } from "../meta/loadout";

const BASE_BLAST_RADIUS = 25;
// One shell should kill infantry through the core of its blast — a single
// 8-second-reload chamber that only wounds made the gun feel useless.
const BLAST_DAMAGE = 3;
const SPEED = 380;
const CHAMBER_SIZE = 1;
const RELOAD_TIME = 8;

export class Howitzer implements Weapon {
  readonly id = "howitzer";
  readonly label = "105MM M102";
  ammo = CHAMBER_SIZE;
  readonly maxAmmo = CHAMBER_SIZE;
  readonly infiniteAmmo = false;
  readonly projectileSpeed = SPEED;

  private cooldownLeft = 0;
  private reloadLeft = 0;
  private readonly blastRadius: number;

  constructor(
    private readonly projectiles: ProjectileManager,
    mods?: Pick<WeaponMods, "blastRadiusMult">,
  ) {
    this.blastRadius = BASE_BLAST_RADIUS * (mods?.blastRadiusMult ?? 1);
  }

  get isReady(): boolean {
    return this.cooldownLeft <= 0 && this.reloadLeft <= 0 && this.ammo > 0;
  }

  getHudState(): WeaponHudState {
    const reloading = this.reloadLeft > 0;
    return {
      weaponId: "howitzer",
      modeLabel: reloading ? "RELOAD" : "BATTERY",
      primaryLabel: reloading ? "BREECH LOAD" : "GUN STATUS",
      primaryValue: reloading ? 1 - this.reloadLeft / RELOAD_TIME : 1,
      primaryText: reloading ? `${this.reloadLeft.toFixed(1)}S` : "READY",
      secondaryLabel: "CHAMBER",
      secondaryText: reloading ? "EMPTY" : "LOADED",
      statusText: reloading ? "RELOADING" : "READY",
      shortText: reloading ? `LOAD ${this.reloadLeft.toFixed(1)}S` : "LOADED",
      zoomText: "WIDE",
    };
  }

  update(dt: number): void {
    if (this.cooldownLeft > 0) this.cooldownLeft = Math.max(0, this.cooldownLeft - dt);
    if (this.reloadLeft > 0) {
      this.reloadLeft = Math.max(0, this.reloadLeft - dt);
      if (this.reloadLeft <= 0) this.ammo = this.maxAmmo;
    }
  }

  fire(origin: THREE.Vector3, dir: THREE.Vector3): FireResult {
    if (!this.isReady) return { fired: false, hitPoint: null, hitObject: null };
    this.ammo -= 1;
    this.cooldownLeft = 0;
    this.reloadLeft = RELOAD_TIME;
    this.projectiles.spawn(origin, dir, SPEED, this.blastRadius, this.id, BLAST_DAMAGE);
    Audio.howitzerShot();
    bus.emit("weapon:fired", { id: this.id });
    return { fired: true, hitPoint: null, hitObject: null };
  }
}
